/*
  Shreya — <video-banner>
  Click-to-play video with a play badge that trails the cursor, plus a
  pausable muted background mode.
*/
(() => {
  if (customElements.get('video-banner')) return;

  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  class VideoBanner extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('.video-banner__frame');
      this.player = this.querySelector('[data-player]');
      this.trigger = this.querySelector('[data-play]');
      this.badge = this.querySelector('[data-badge]');
      this.bgToggle = this.querySelector('[data-bg-toggle]');

      if (this.trigger) {
        this.trigger.addEventListener('click', () => this.play());
        if (this.classList.contains('video-banner--btn-cursor') && this.badge) this.followCursor();
      }
      if (this.dataset.mode === 'background') this.setupBackground();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      if (this.observer) this.observer.disconnect();
    }

    /* ---------- click to play ---------- */
    play() {
      if (this.classList.contains('is-playing')) return;
      const template = this.player && this.player.querySelector('template');
      if (!template) return;
      this.player.appendChild(template.content.cloneNode(true));
      this.classList.add('is-playing');
      cancelAnimationFrame(this.raf);

      const media = this.player.querySelector('video, iframe');
      if (media && media.tagName === 'VIDEO') {
        const p = media.play();
        if (p && p.catch) p.catch(() => {});
      }
      if (media) {
        media.setAttribute('tabindex', '0');
        setTimeout(() => media.focus({ preventScroll: true }), 60);
      }
    }

    /* ---------- trailing badge ---------- */
    followCursor() {
      if (!finePointer()) return;
      this.classList.add('has-cursor');
      const state = { x: 0, y: 0, tx: 0, ty: 0 };
      const ease = reduced() ? 1 : 0.16;

      const tick = () => {
        state.x += (state.tx - state.x) * ease;
        state.y += (state.ty - state.y) * ease;
        this.badge.style.setProperty('--bx', `${state.x.toFixed(2)}px`);
        this.badge.style.setProperty('--by', `${state.y.toFixed(2)}px`);
        if (Math.abs(state.tx - state.x) > 0.3 || Math.abs(state.ty - state.y) > 0.3) {
          this.raf = requestAnimationFrame(tick);
        } else {
          this.raf = null;
        }
      };
      const kick = () => { if (!this.raf) this.raf = requestAnimationFrame(tick); };

      this.trigger.addEventListener('pointermove', (e) => {
        if (e.pointerType !== 'mouse') return;
        const r = this.trigger.getBoundingClientRect();
        state.tx = e.clientX - r.left - r.width / 2;
        state.ty = e.clientY - r.top - r.height / 2;
        this.classList.add('is-hovering');
        kick();
      });
      this.trigger.addEventListener('pointerleave', () => {
        state.tx = 0;
        state.ty = 0;
        this.classList.remove('is-hovering');
        kick();
      });
    }

    /* ---------- muted background ---------- */
    setupBackground() {
      const video = this.player && this.player.querySelector('video');
      if (!video) return;
      video.muted = true;
      this.userPaused = reduced();
      this.inView = true;
      const apply = () => {
        if (this.inView && !this.userPaused) {
          const p = video.play();
          if (p && p.catch) p.catch(() => {});
        } else {
          video.pause();
        }
        if (this.bgToggle) {
          this.bgToggle.classList.toggle('is-paused', this.userPaused);
          this.bgToggle.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
          this.bgToggle.setAttribute('aria-label', this.userPaused ? this.bgToggle.dataset.labelPlay : this.bgToggle.dataset.labelPause);
        }
      };
      if (this.bgToggle) {
        this.bgToggle.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          apply();
        });
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting;
          apply();
        }, { threshold: 0.1 });
        this.observer.observe(this);
      }
      apply();
    }
  }

  customElements.define('video-banner', VideoBanner);
})();
